import React from 'react';
import { IntegrationField } from '../../types';
import { Edit, Trash2, Info, Code } from 'lucide-react';
import SoarFieldMappingItem from './SoarFieldMappingItem';

interface IntegrationFieldItemProps {
  field: IntegrationField;
  onEdit: (field: IntegrationField) => void;
  onDelete: (fieldKey: string) => void;
}

const IntegrationFieldItem: React.FC<IntegrationFieldItemProps> = ({
  field,
  onEdit,
  onDelete
}) => {
  // Function to get the appropriate field type label
  const getFieldTypeLabel = (type: string) => {
    switch (type) {
      case 'text': return 'Text';
      case 'number': return 'Number';
      case 'secret': return 'Secret';
      case 'url': return 'URL';
      case 'multi-select': return 'Multi-Select';
      case 'soar-mapping': return 'Soar Field Mapping';
      default: return type;
    }
  };

  // Function to get appropriate icon for field type
  const getFieldTypeIcon = () => {
    switch (field.type) {
      case 'soar-mapping':
        return <Code size={16} className="text-blue-400 mr-2" />;
      default:
        return <Info size={16} className="text-text-muted-dark mr-2" />;
    }
  };

  return (
    <div className="border border-border-dark rounded-lg p-4 mb-4 bg-card-dark">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center mb-1">
            {getFieldTypeIcon()}
            <h3 className="font-medium">{field.label}</h3>
            {field.is_required && (
              <span className="ml-2 text-xs bg-status-danger/20 text-status-danger px-2 py-0.5 rounded-full">
                Required
              </span>
            )}
          </div>
          <div className="text-sm text-text-muted-dark mb-3 flex items-center">
          <code className="px-2 py-0.5 rounded text-xs text-white">


              {field.field_key}
            </code>
            <span className="mx-2">•</span>
            <span>{getFieldTypeLabel(field.type)}</span>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => onEdit(field)}
            className="p-1.5 hover:bg-background-hover rounded transition-colors"
            title="Edit Field"
          >
            <Edit size={16} className="text-text-muted-dark hover:text-text-light" />
          </button>
          
          <button
            type="button"
            onClick={() => onDelete(field.field_key)}
            className="p-1.5 hover:bg-background-hover rounded transition-colors"
            title="Delete Field"
          >
            <Trash2 size={16} className="text-text-muted-dark hover:text-status-danger" />
          </button>
        </div>
      </div>
      
      {/* Field-specific content based on type */}
      {field.type === 'multi-select' && field.options && field.options.length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-text-muted-dark mb-1">Options:</div>
          <div className="flex flex-wrap gap-1.5">
            {field.options.map((option, index) => (
              <span 
                key={index}
                className="text-xs text-primary-300 px-2 py-1 rounded border border-primary-300/30"
              >
                {option}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Display field mapping for soar-mapping type */}
      {field.type === 'soar-mapping' && field.field_mapping && (
        <div className="mt-2">
          <SoarFieldMappingItem fieldMapping={field.field_mapping} />
        </div>
      )}
      
      {/* Display default value for applicable field types */}
      {field.default_value && field.type !== 'multi-select' && field.type !== 'soar-mapping' && (
        <div className="mt-2">
          <div className="text-xs text-text-muted-dark mb-1">Default Value:</div>
          <div className="text-sm bg-background-hover px-2 py-1 rounded inline-block">
            {field.default_value}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationFieldItem; 